"use client"
import { useCallback, useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { PLAN_NODE_IDS, getSelectedPlanFromPath, type PlanId } from '@/lib/branchTypes'
import { EPILOGUE_HEADING_ID, type PlanATimelineDiagramProps } from './planATimelineDiagramModel'
import { setEscapePerspectiveNavigator } from './timelineDiagramNavigation'

// Reconstructs the playbook callbacks (selectPlan / setEpilogueExpanded /
// currentPlan) from the URL hash, for end-of-scenario nav UI rendered
// inside generic markdown or ScenarioEndFooter, where those callbacks
// aren't threaded down (ScenarioEndNavGrid, PlanATimelineDiagramStandalone).
//
// The hash is the same channel the playbook already listens to:
// useAnchorDeepLink resolves a #plan-x-root / #playbook-epilogue hash,
// expands the branch path, mounts the epilogue, and scrolls. So writing the
// hash here drives the exact same navigation a playbook-wired button would,
// without touching the Markdown component's props.
export const useStandaloneDiagramProps = (
  // True when the caller caps the INLINE Plan A continuation shown after
  // finishing another plan (ContinueWithPlan). There Plan A's content is
  // already mounted even though the hash still points at the other plan's
  // root, so we report currentPlan='A' to skip the plan switch — letting the
  // Epilogue button expand the inline epilogue IN PLACE instead of
  // navigating to the real #plan-a-root path.
  inlinePlanAContinuation = false,
): PlanATimelineDiagramProps => {
  const router = useRouter()

  // On a `?perspective=` page the playbook tree (and its hash deep-link
  // machinery) isn't mounted, so a hash change drives no navigation. The
  // shared navigation logic detects that and delegates the "escape to
  // the default scenario" jump to this router-aware push, which drops
  // `perspective` and carries the clicked target as the hash; the
  // freshly-served playbook deep-links to it on load.
  // (See timelineDiagramNavigation.)
  useEffect(() => {
    setEscapePerspectiveNavigator((targetHash) => {
      // Leave /perspective/[category] for the main playbook, carrying the
      // clicked target as the hash; the freshly-served playbook deep-links
      // to it on load.
      router.push(`/#${targetHash}`)
    })
    return () => setEscapePerspectiveNavigator(null)
  }, [router])

  // Active plan = whatever plan node the hash points at (null on fresh
  // load). It's only used to skip a redundant nav when you click the plan
  // you're already on, so hash-derived is enough.
  //
  // In the inline Plan A continuation the hash still names the OTHER
  // plan's root (you got here via e.g. Plan B) but Plan A's content is
  // mounted, so pin currentPlan to 'A'. That makes the Epilogue button
  // skip selectPlan('A') (which would jump to the real Plan A path) and
  // just fire the #playbook-epilogue hash, expanding inline in place.
  const [currentPlan, setCurrentPlan] = useState<PlanId | null>(() =>
    inlinePlanAContinuation
      ? 'A'
      : typeof window === 'undefined'
        ? null
        : getSelectedPlanFromPath([window.location.hash.replace(/^#/, '')]),
  )

  // Setting location.hash fires `hashchange`, which the playbook's
  // deep-link handler turns into a path expansion + scroll. Re-clicking
  // the same plan won't re-fire it, so nudge it manually then. (On a
  // `?perspective=` page the shared navigation short-circuits to the
  // router push above before this runs.)
  const navigateToHash = useCallback((id: string) => {
    if (window.location.hash === `#${id}`) {
      window.dispatchEvent(new HashChangeEvent('hashchange'))
    } else {
      window.location.hash = id
    }
  }, [])

  const selectPlan = useCallback(
    (planId: PlanId) => {
      setCurrentPlan(planId)
      navigateToHash(PLAN_NODE_IDS[planId])
    },
    [navigateToHash],
  )

  // The epilogue lives behind a collapsed section the playbook mounts
  // only when the hash is #playbook-epilogue (useAnchorDeepLink calls
  // onExpandEpilogue). So "expand" == route to that hash; "collapse" is
  // a no-op (navigating elsewhere already moves focus away).
  const setEpilogueExpanded = useCallback(
    (expanded: boolean) => {
      if (expanded) navigateToHash(EPILOGUE_HEADING_ID)
    },
    [navigateToHash],
  )

  return { currentPlan, selectPlan, setEpilogueExpanded }
}
